/**
 * What an entity assistant is told about the form it sits beside — the `contextSpec` sent when its conversation is
 * created and again with every message.
 *
 * The engine renders it into the assistant's prompt, one hook per subject, and each hook reads a fixed shape:
 *
 *   - tool, template, employee (`engine/src/agentic/hooks/render-{tool,template,workforce}-state.hook.ts`): the form
 *     under `current`, the wizard step beside it. The form was sent flat instead, so these three assistants were
 *     never shown it and answered "I can't see the form state".
 *   - workflow (`render-workflow-state.hook.ts`): the page already builds the shape (`workflowState`, the selected
 *     step, the mode), so it is passed through.
 *   - anything else has no state hook and is passed through.
 *
 * `extras` are what the page knows that is not a form field — an employee's scope, the marketplace listing a draft
 * was started from — and sit at the top level, where the hooks read them.
 */
import type { AssistantSubjectType } from './EntityAssistantDrawer.js';

const FORM_UNDER_CURRENT: ReadonlySet<AssistantSubjectType> = new Set([
  'tool',
  'template',
  'daemon-draft',
  'analysis_template',
]);

export function assistantContextSpec(
  subjectType: AssistantSubjectType,
  formValues: Record<string, unknown>,
  options: { currentStepKey?: string; extras?: Record<string, unknown> } = {},
): Record<string, unknown> {
  const beside = {
    ...options.extras,
    ...(options.currentStepKey ? { currentStepKey: options.currentStepKey } : {}),
  };
  return FORM_UNDER_CURRENT.has(subjectType) ? { current: formValues, ...beside } : { ...formValues, ...beside };
}
